import {
    ChangeDetectorRef, Directive,
    ElementRef,
    EventEmitter,
    HostListener,
    Input,
    OnDestroy,
    OnInit,
    Output,
    ViewChild
} from "@angular/core";
import {FUIAbstractInput} from "../../framework/abstract-input";
import {Subscription} from "rxjs";
import {TranslateService} from "@ngx-translate/core";
import {ColorPickerDirective} from "ngx-color-picker";
import {SafeChangeDetectorRef} from "../safe-change-detector-ref";
import {FUIEventsService} from "../../../events/events.service";

@Directive()
export abstract class FUIAbstractColorInput extends FUIAbstractInput implements OnInit, OnDestroy {
    @Input() okLabel: string;
    @Input() resetLabel: string;
    @Input() allowAlpha: boolean = true;
    @Input() textColor: string;
    @Input() showClearIcon: boolean = false;
    @Output() onClear: EventEmitter<void> = new EventEmitter<void>();

    color: string = "#ffffff";

    // color picker not always picking up result when binding to the result of the translate pipe
    translatedOkLabel: string;
    translatedResetLabel: string;

    private _$click: Subscription;
    private _popped: boolean;
    private clicking = false;

    constructor(public elementRef: ElementRef,
                protected eventsService: FUIEventsService,
                protected translateService: TranslateService,
                changeDetectorRef: ChangeDetectorRef) {
        super(changeDetectorRef);
    }

    ngOnInit(): void {
        this.translateService.get(this.okLabel).subscribe(translation => {
            this.translatedOkLabel = translation;
            this.translateService.get(this.resetLabel).subscribe(translation => this.translatedResetLabel = translation);
        });
        this._$click = this.eventsService.click$.subscribe(
            (click) => {
                if (!this.clicking && this.popped && !this.elementRef.nativeElement.contains(click.target)) {
                    this.popped = false;
                }
            }
        );
    }

    @ViewChild("colorPicker", {read: ColorPickerDirective})
    set colorPickerDirective(colorPickerDirective: ColorPickerDirective) {
        if (colorPickerDirective) {
            const component: any = (<any> colorPickerDirective).cmpRef._component;
            if (component && component.cdr) {
                component.cdr = new SafeChangeDetectorRef(component.cdr);
            }
        }
    }

    override ngOnDestroy(): void {
        super.ngOnDestroy();
        this._$click.unsubscribe();
    }

    /**
     *
     * @returns {boolean}
     */
    get popped(): boolean {
        return this._popped;
    }

    /**
     *
     * @param {boolean} popped
     */
    set popped(popped: boolean) {
        this._popped = popped;
        this.focussed = popped;
        this.focussed$.emit(this.focussed);
    }

    /**
     *
     * @param {boolean} popped
     */
    public pop(popped: boolean): void {
        this.popped = popped;
    }

    /**
     * Click action is different to other controls, it only wants to pop up it's dropdown, not focus on the underlying input
     */
    public override click(): void {
        this.clicking = true;
        setTimeout(() => this.clicking = false);
        this.popped = !this.popped;
    }

    /**
     * If a user presses enter or space while focussed on the element,
     * use the click method to apply the relevant interaction
     * @param {KeyboardEvent} keyboardEvent
     */
    @HostListener("keypress", ["$event"])
    public keypress(keyboardEvent: KeyboardEvent) {
        if (!this.disabled) {
            if (keyboardEvent.code === "Enter" || keyboardEvent.code === "Space" || keyboardEvent.key === "Enter" || keyboardEvent.key === " ") {
                this.click();
                keyboardEvent.preventDefault();
                keyboardEvent.stopPropagation();
            }
        }
    }

    colorSelected(color: string) {
        super.value = color;
        this.popped = false;
    }

    override get value(): any {
        return super.value;
    }

    override set value(value: any) {
        if (value === "#00000000") {
            this.color = "#FFFFFFFF";
        } else if (value) {
            this.color = value;
        }
        super.value = value;
    }

    override writeValue(value: any): void {
        if (value === "#00000000") {
            this.color = "#FFFFFFFF";
        } else if (value) {
            this.color = value;
        }
        super.writeValue(value);
    }

    clear(event: Event): void {
        event.stopPropagation();
        this.onClear.next();
    }
}
